import React, {
  useRef,
  useState,
  useEffect,
} from "react";
import Header from "../components/Header";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCreative } from "swiper/modules";

import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import Swal from "sweetalert2";

import "swiper/css";
import 'swiper/css/effect-creative';

import {
  FaWhatsapp,
  FaEnvelope,
  FaPaperPlane,
  FaCheckCircle,
} from "react-icons/fa";

import DemoAudio from "../components/DemoAudio";


function Home() {

  /* =====================================================
      CONTACT FORM
  ===================================================== */

  const formRef = useRef(null);

  const recaptchaRef = useRef(null);

  const formLoadedAt = useRef(Date.now());


  const [isSending, setIsSending] =
    useState(false);

  const [captchaToken, setCaptchaToken] =
    useState("");

  const [honeypot, setHoneypot] =
    useState("");


  /* =====================================================
      ENV
  ===================================================== */

const EMAILJS_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_SERVICE_ID;

const EMAILJS_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

const EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY;


  /* =====================================================
      VALIDAR CONFIGURACIÓN
  ===================================================== */

  useEffect(() => {

    if (
      !EMAILJS_SERVICE_ID ||
      !EMAILJS_TEMPLATE_ID ||
      !EMAILJS_PUBLIC_KEY ||
      !RECAPTCHA_SITE_KEY
    ) {

      console.error(
        "Faltan variables de configuración para EmailJS o reCAPTCHA."
      );

    }

  }, []);


  /* =====================================================
      FORM SUBMIT
  ===================================================== */

  const handleContactSubmit = async (event) => {

    event.preventDefault();


    /* -------------------------------------------------
       EVITAR DOBLE ENVÍO
    ------------------------------------------------- */

    if (isSending) {
      return;
    }


    /* -------------------------------------------------
       HONEYPOT
       Un humano nunca debería llenar este campo.
    ------------------------------------------------- */

    if (honeypot.trim() !== "") {

      console.warn(
        "Envío bloqueado por honeypot."
      );

      return;
    }


    /* -------------------------------------------------
       TIEMPO MÍNIMO
       Bots suelen completar formularios instantáneamente.
    ------------------------------------------------- */

    const secondsOnForm =
      (Date.now() - formLoadedAt.current) / 1000;


    if (secondsOnForm < 3) {

      await Swal.fire({
        icon: "warning",

        title: "Espera un momento",

        text:
          "Por favor revisa la información antes de enviar tu solicitud.",

        confirmButtonText:
          "Entendido",

        buttonsStyling:
          false,

        customClass: {
          popup:
            "mp-alert",

          confirmButton:
            "mp-alert__button",
        },
      });

      return;
    }


    /* -------------------------------------------------
       CAPTCHA OBLIGATORIO
    ------------------------------------------------- */

    if (!captchaToken) {

      await Swal.fire({
        icon: "warning",

        title: "Confirma que eres humano",

        text:
          "Completa la verificación reCAPTCHA antes de enviar tu solicitud.",

        confirmButtonText:
          "Entendido",

        buttonsStyling:
          false,

        customClass: {
          popup:
            "mp-alert",

          confirmButton:
            "mp-alert__button",
        },
      });

      return;
    }


    /* -------------------------------------------------
       VALIDACIÓN NATIVA HTML
    ------------------------------------------------- */

    if (
      !formRef.current ||
      !formRef.current.checkValidity()
    ) {

      formRef.current?.reportValidity();

      return;
    }


    /* -------------------------------------------------
       COOLDOWN
    ------------------------------------------------- */

    const LAST_SEND_KEY =
      "mp-contact-last-send";

    const COOLDOWN_MS =
      60 * 1000;


    const lastSend =
      Number(
        localStorage.getItem(
          LAST_SEND_KEY
        ) || 0
      );


    if (
      lastSend &&
      Date.now() - lastSend < COOLDOWN_MS
    ) {

      const secondsRemaining =
        Math.ceil(
          (
            COOLDOWN_MS -
            (Date.now() - lastSend)
          ) / 1000
        );


      await Swal.fire({
        icon: "info",

        title:
          "Tu mensaje ya fue recibido",

        text:
          `Espera ${secondsRemaining} segundos antes de realizar otro envío.`,

        confirmButtonText:
          "Entendido",

        buttonsStyling:
          false,

        customClass: {
          popup:
            "mp-alert",

          confirmButton:
            "mp-alert__button",
        },
      });

      return;
    }


    try {

      setIsSending(true);


      /* =================================================
         EMAIL JS
      ================================================= */

      await emailjs.sendForm(

        EMAILJS_SERVICE_ID,

        EMAILJS_TEMPLATE_ID,

        formRef.current,

        {
          publicKey:
            EMAILJS_PUBLIC_KEY,
        }

      );


      /* -------------------------------------------------
         GUARDAR ÚLTIMO ENVÍO
      ------------------------------------------------- */

      localStorage.setItem(
        LAST_SEND_KEY,
        Date.now().toString()
      );


      /* -------------------------------------------------
         LIMPIAR FORMULARIO
      ------------------------------------------------- */

      formRef.current.reset();

      recaptchaRef.current?.reset();

      setCaptchaToken("");

      setHoneypot("");

      formLoadedAt.current =
        Date.now();


      /* =================================================
         SUCCESS
      ================================================= */

      await Swal.fire({

        icon:
          "success",

        title:
          "¡Mensaje enviado!",

        html: `
          <p class="mp-alert__message">
            Gracias por contactarme.
            Recibí la información de tu proyecto y
            me pondré en contacto contigo lo antes posible.
          </p>
        `,

        confirmButtonText:
          "Perfecto",

        buttonsStyling:
          false,

        showCloseButton:
          true,

        customClass: {

          popup:
            "mp-alert mp-alert--success",

          title:
            "mp-alert__title",

          confirmButton:
            "mp-alert__button",

          closeButton:
            "mp-alert__close",

        },

      });


    } catch (error) {

      console.error(
        "Error EmailJS:",
        error
      );


      /* =================================================
         ERROR
      ================================================= */

      await Swal.fire({

        icon:
          "error",

        title:
          "No pudimos enviar tu mensaje",

        html: `
          <p class="mp-alert__message">
            Ocurrió un problema al enviar tu solicitud.
            Puedes intentarlo nuevamente o contactarme
            directamente por WhatsApp.
          </p>
        `,

        confirmButtonText:
          "Intentar de nuevo",

        buttonsStyling:
          false,

        customClass: {

          popup:
            "mp-alert",

          title:
            "mp-alert__title",

          confirmButton:
            "mp-alert__button",

        },

      });


      recaptchaRef.current?.reset();

      setCaptchaToken("");


    } finally {

      setIsSending(false);

    }

  };

  return (
    <>
      <Header />

      <main className="home-page">

        {/* =====================================================
            HERO / PORTADA
        ===================================================== */}
        <section
          id="quien-soy"
          className="home-section home-hero"
        >
          {/* Elementos decorativos */}
            <div className="home-hero__glow home-hero__glow--one" />

            <div className="home-hero__glow home-hero__glow--top-right" />

            <div className="home-hero__glow home-hero__glow--two" />

            <div className="home-container">

                <div className="home-hero__grid">

                {/* =================================================
                    COLUMNA IZQUIERDA
                ================================================= */}
                <div className="home-hero__content">

                    {/* <div className="home-hero__eyebrow hero-animate hero-animate--1">
                    <span className="home-hero__eyebrow-line" />

                    <span>
                        Voz que conecta
                    </span>
                    </div> */}


                    <h1 className="home-hero__title hero-animate hero-animate--2">
                    Mónica 
                    Palacios
                    </h1>


                    <p className="home-hero__description hero-animate hero-animate--3">
                    Locutora comercial con base en Ciudad de México, comunicóloga con especialización en radio y televisión y profesional de la voz en formación constante.
                    </p>

                    <p className="home-hero__description hero-animate hero-animate--3">
                    Llevo más de cinco años preparándome en técnica, interpretación y desarrollo vocal. Cuento con herramientas que me permiten dar intención, sensibilidad y claridad a cada proyecto.
                    </p>

                    <p className="home-hero__description hero-animate hero-animate--3">
                      Mi formación como comunicóloga me ha dado una perspectiva muy valiosa dentro de este camino. Me ayuda a entender que detrás de cada guión hay una intención, una marca, una audiencia y un objetivo específico. Por eso, además de interpretar un texto, busco comprender lo que el cliente realmente quiere comunicar para conectar mejor con su mensaje y audiencia.

                    </p>

                    <p className="home-hero__description hero-animate hero-animate--3">
                      Mi objetivo no es sólo aportar una voz que suene bien, sino que transmita de forma auténtica, empática y efectiva. Creo en el poder de la voz como un puente entre las ideas y las emociones, y disfruto trabajar cada proyecto con profesionalismo, atención al detalle y compromiso.

                    </p>

                    <p className="home-hero__description hero-animate hero-animate--3">
                      Encontrarás en mí, dedicación, preparación y entusiasmo, además de la convicción que una buena locución nace de saber escuchar, interpretar y comunicar con intención.
                    </p>


                    {/* =============================================
                        CTA
                    ============================================= */}
                    <div className="home-hero__actions hero-animate hero-animate--4">

                    <button
                        type="button"
                        className="home-hero__button home-hero__button--primary"
                        onClick={() => {
                        document
                            .getElementById("demos")
                            ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            });
                        }}
                    >
                        Escuchar demos

                        <span className="home-hero__button-arrow">
                        →
                        </span>
                    </button>


                    <a
                        href="mailto:monicapalaciosgmz@gmail.com"
                        className="home-hero__button home-hero__button--secondary"
                    >
                        Enviar correo
                    </a>

                    </div>


                    {/* =============================================
                        CARDS
                    ============================================= */}
                    {/* <div className="home-hero__services hero-animate hero-animate--5">

                    <article className="home-hero__service-card">
                        <div className="home-hero__service-icon">
                        C
                        </div>

                        <div>
                        <h3>
                            Comercial
                        </h3>

                        <p>
                            Spots, campañas, social ads y promos.
                        </p>
                        </div>
                    </article>


                    <article className="home-hero__service-card">
                        <div className="home-hero__service-icon">
                        I
                        </div>

                        <div>
                        <h3>
                            Institucional
                        </h3>

                        <p>
                            Videos corporativos, capacitación y narración.
                        </p>
                        </div>
                    </article>


                    <article className="home-hero__service-card">
                        <div className="home-hero__service-icon">
                        D
                        </div>

                        <div>
                        <h3>
                            Dirección
                        </h3>

                        <p>
                            Lectura con intención, marca y audiencia.
                        </p>
                        </div>
                    </article>

                    </div> */}

                </div>


                {/* =================================================
                    COLUMNA DERECHA / CARRUSEL
                ================================================= */}
                <div className="home-hero__visual hero-animate hero-animate--visual">

                  <div className="home-hero__image-card">

                    <div className="home-hero__image-wrapper">

                      <Swiper
                        modules={[Autoplay, EffectCreative]}
                        effect="creative"
                        creativeEffect={{
                          prev: {
                            shadow: true,
                            translate: ['-20%', 0, -1],
                          },
                          next: {
                            translate: ['100%', 0, 0],
                          },
                        }}
                        loop={true}
                        speed={1200}
                        allowTouchMove={false}
                        autoplay={{
                          delay: 4500,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: false,
                        }}
                        className="home-hero__swiper"
                      >

                        <SwiperSlide>
                          <img
                            src="/assets/img/monica-palacios-1.png"
                            alt="Mónica Palacios en sesión profesional"
                            className="home-hero__image"
                          />
                        </SwiperSlide>

                        <SwiperSlide>
                          <img
                            src="/assets/img/monica-palacios-2.jpg"
                            alt="Mónica Palacios en estudio de locución"
                            className="home-hero__image"
                          />
                        </SwiperSlide>

                        <SwiperSlide>
                          <img
                            src="/assets/img/monica-palacios-3.jpg"
                            alt="Mónica Palacios durante una sesión de voz"
                            className="home-hero__image"
                          />
                        </SwiperSlide>

                      </Swiper>

                      <div className="home-hero__image-overlay" />

                    </div>


                    {/* =============================================
                        CARD SOBRE IMAGEN
                    ============================================= */}
                    <div className="home-hero__voice-card">

                      {/* <span className="home-hero__voice-label">
                        Soy una voz natural, cercana y confiable
                      </span> */}

                      <h2>
                        Soy una voz natural, cercana y confiable
                      </h2>

                    </div>

                  </div>

                </div>

                </div>

            </div>
        </section>

        {/* =====================================================
            DEMOS
        ===================================================== */}
        <section
          id="demos"
          className="home-section home-section--demos"
        >
          <div className="home-container">

            {/* =================================================
                ENCABEZADO
            ================================================= */}
            <div className="demos-section__header">

              <div className="demos-section__eyebrow">
                <span className="demos-section__eyebrow-line" />

                <span>
                  Escucha mi trabajo
                </span>
              </div>

              <div className="demos-section__heading">

                <h2>
                  Demos de voz
                </h2>

                <p>
                  Dos estilos de locución pensados para comunicar
                  con intención, claridad y personalidad.
                </p>

              </div>

            </div>


            {/* =================================================
                GRID
            ================================================= */}
            <div className="demos-section__grid">

              {/* =================================================
                  DEMO COMERCIAL
              ================================================= */}
              <article className="demo-card demo-card--commercial">

                {/* Decoración */}
                <div className="demo-card__decoration" />


                <div className="demo-card__content">

                  <span className="demo-card__tag">
                    Demo comercial
                  </span>


                  <div className="demo-card__main">

                    <h3>
                      Para campañas, marcas y
                      <br className="demo-card__desktop-break" />
                      contenido publicitario.
                    </h3>

                    <p>
                      Una muestra de mi registro comercial y de las distintas posibilidades de mi voz: desde tonos cercanos, naturales y conversacionales, hasta interpretaciones con mayor energía, intención y dinamismo.
                    </p>
                    <p>
                      Mi voz se adapta especialmente a mensajes que buscan sentirse actuales, frescos y auténticos, manteniendo siempre claridad y conexión con quien escucha.
                    </p>

                  </div>


                  {/* =============================================
                      AUDIO UI
                  ============================================= */}
                  <DemoAudio
                    src="/assets/audio/DEMO MONICA PALACIOS V3.mp3"
                    label="Demo narrativo corporativo de Mónica Palacios"
                  />


                  <div className="demo-card__footer">

                    <span className="demo-card__footer-label">
                      Escucha una muestra
                    </span>

                    <span className="demo-card__footer-line" />

                  </div>

                </div>

              </article>


              {/* =================================================
                  DEMO INSTITUCIONAL
              ================================================= */}
              <article className="demo-card demo-card--institutional">

                {/* Decoración */}
                <div className="demo-card__decoration" />


                <div className="demo-card__content">

                  <span className="demo-card__tag">
                    Demo institucional
                  </span>


                  <div className="demo-card__main">

                    <h3>
                      Para mensajes corporativos,
                      <br className="demo-card__desktop-break" />
                      educativos y narrativos.
                    </h3>

                    <p>
                      En este demo exploro mi registro narrativo e institucional, con una voz cálida, clara y cercana que puede transitar entre la naturalidad, la sensibilidad y un tono más profesional.
                    </p>
                    <p>
                      Un estilo pensado para contenidos que necesitan comunicar con confianza y credibilidad, sin perder cercanía ni sentirse rígidos o excesivamente serios.
                    </p>

                  </div>


                  {/* =============================================
                      AUDIO UI
                  ============================================= */}
                  <DemoAudio
                    src="/assets/audio/DEMO MONICA PALACIOS NARRATIVO CORPORATIVO.wav"
                    label="Demo narrativo corporativo de Mónica Palacios"
                  />


                  <div className="demo-card__footer">

                    <span className="demo-card__footer-label">
                      Escucha una muestra
                    </span>

                    <span className="demo-card__footer-line" />

                  </div>

                </div>

              </article>

            </div>

          </div>
        </section>

        <div className="contact-footer-shell">

        {/* GLOWS COMPARTIDOS */}
        <div className="contact-footer-shell__glow contact-footer-shell__glow--one" />
        <div className="contact-footer-shell__glow contact-footer-shell__glow--two" />
        <div className="contact-footer-shell__glow contact-footer-shell__glow--bottom" />
        {/* =====================================================
            CONTACTO
        ===================================================== */}
        <section
          id="contacto"
          className="home-section home-section--contacto"
        >

          <div className="contact-section__line-art">
            <span />
            <span />
            <span />
          </div>


          <div className="home-container">

            <div className="contact-section__grid">

              {/* =================================================
                  COLUMNA IZQUIERDA
              ================================================= */}
              <div className="contact-section__content">

                <div className="contact-section__eyebrow">
                  <span className="contact-section__eyebrow-line" />

                  <span>
                    Contacto
                  </span>
                </div>


                <h2 className="contact-section__title">
                  ¿Tienes un proyecto 
                  en puerta?
                </h2>


                <p className="contact-section__description">
                  Cuéntame qué necesitas comunicar y trabajemos
                  en una voz que conecte con tu marca, audiencia
                  y objetivo.
                </p>


                <p className="contact-section__secondary-text">
                  Puedes compartir tu guion, solicitar disponibilidad,
                  pedir una cotización o simplemente contarme un poco
                  sobre tu proyecto.
                </p>


                {/* =============================================
                    CONTACT ACTIONS
                ============================================= */}
                <div className="contact-section__actions">

                  <a
                    href="https://wa.me/525530098562"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-section__button contact-section__button--primary"
                  >
                    <FaWhatsapp />

                    <span>
                      WhatsApp
                    </span>
                  </a>


                  <a
                    href="mailto:monicapalaciosgmz@gmail.com"
                    className="contact-section__button contact-section__button--secondary"
                  >
                    <FaEnvelope />

                    <span>
                      Enviar correo
                    </span>
                  </a>

                </div>


                {/* =============================================
                    MICRO COPY
                ============================================= */}
                {/* <div className="contact-section__availability">

                  <span className="contact-section__availability-dot" />

                  <span>
                    Disponible para proyectos comerciales e institucionales
                  </span>

                </div> */}

              </div>


              {/* =================================================
                    FORMULARIO
                ================================================= */}
                <div className="contact-form-wrapper">

                  <div className="contact-form__header">

                    <span>
                      Cuéntame sobre tu proyecto
                    </span>

                    <p>
                      Déjame tus datos y me pondré en contacto contigo.
                    </p>

                  </div>


                  <form
                    ref={formRef}
                    className="contact-form"
                    onSubmit={handleContactSubmit}
                    noValidate={false}
                  >

                    {/* =============================================
                        HONEYPOT ANTI-SPAM
                    ============================================= */}
                    <div
                      className="contact-form__honeypot"
                      aria-hidden="true"
                    >
                      <label htmlFor="contact-website">
                        Sitio web
                      </label>

                      <input
                        id="contact-website"
                        name="website"
                        type="text"
                        value={honeypot}
                        onChange={(event) =>
                          setHoneypot(
                            event.target.value
                          )
                        }
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>


                    {/* =============================================
                        NOMBRE + CORREO
                    ============================================= */}
                    <div className="contact-form__row">

                      <div className="contact-form__group">

                        <label htmlFor="contact-name">
                          Nombre
                        </label>

                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          placeholder="Tu nombre"
                          autoComplete="name"

                          minLength={2}
                          maxLength={80}

                          pattern="^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' .-]{2,80}$"

                          required
                        />

                      </div>


                      <div className="contact-form__group">

                        <label htmlFor="contact-email">
                          Correo
                        </label>

                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          placeholder="correo@ejemplo.com"
                          autoComplete="email"

                          maxLength={120}

                          required
                        />

                      </div>

                    </div>


                    {/* =============================================
                        TIPO DE PROYECTO
                    ============================================= */}
                    <div className="contact-form__group">

                      <label htmlFor="contact-project">
                        Tipo de proyecto
                      </label>

                      <select
                        id="contact-project"
                        name="project"
                        defaultValue=""
                        required
                      >

                        <option
                          value=""
                          disabled
                        >
                          Selecciona una opción
                        </option>


                        <option value="Locución comercial">
                          Locución comercial
                        </option>


                        <option value="Locución institucional">
                          Locución institucional
                        </option>


                        <option value="Narración">
                          Narración
                        </option>


                        <option value="Otro proyecto">
                          Otro proyecto
                        </option>

                      </select>

                    </div>


                    {/* =============================================
                        MENSAJE
                    ============================================= */}
                    <div className="contact-form__group">

                      <label htmlFor="contact-message">
                        Cuéntame qué necesitas grabar
                      </label>

                      <textarea
                        id="contact-message"
                        name="message"

                        rows="6"

                        minLength={10}
                        maxLength={2000}

                        placeholder="Platícame brevemente sobre tu proyecto, duración estimada, estilo de voz, fecha de entrega o cualquier información importante."

                        required
                      />

                      <span className="contact-form__field-hint">
                        Incluye duración, estilo de voz y fecha de entrega si ya los conoces.
                      </span>

                    </div>


                    {/* =============================================
                        CAPTCHA
                    ============================================= */}
                    <div className="contact-form__captcha">

                      <ReCAPTCHA
                        ref={recaptchaRef}

                        sitekey={
                          RECAPTCHA_SITE_KEY
                        }

                        onChange={(token) => {
                          setCaptchaToken(
                            token || ""
                          );
                        }}

                        onExpired={() => {
                          setCaptchaToken("");
                        }}

                        onErrored={() => {
                          setCaptchaToken("");
                        }}
                      />

                    </div>


                    {/* =============================================
                        SUBMIT
                    ============================================= */}
                    <button
                      type="submit"
                      className={`contact-form__submit ${
                        isSending
                          ? "contact-form__submit--loading"
                          : ""
                      }`}
                      disabled={isSending}
                    >

                      {isSending ? (

                        <>
                          <span className="contact-form__spinner" />

                          <span>
                            Enviando solicitud...
                          </span>
                        </>

                      ) : (

                        <>
                          <span>
                            Enviar solicitud
                          </span>

                          <FaPaperPlane />
                        </>

                      )}

                    </button>


                    <p className="contact-form__privacy">
                      La información enviada será utilizada únicamente
                      para responder tu solicitud.
                    </p>

                  </form>

                </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            FOOTER
        ===================================================== */}
        <footer className="site-footer">

          <div className="home-container">

            <div className="site-footer__content">

              <p className="site-footer__copyright">
                © {new Date().getFullYear()} Mónica Palacios.
                Todos los derechos reservados.
              </p>

            </div>

          </div>

        </footer>

        </div>

      </main>
    </>
  );
}

export default Home;